'use client';

import { useLayoutEffect, useRef } from 'react';

const DURATION = 420;
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * The face that slides between the household strip and the hero when a
 * person is picked. Every face carries `data-face={contactId}`; before the
 * switch we note where each one is, after React has moved them we animate
 * each from its old box to its new one (translate + scale, Web Animations).
 * Plain FLIP: nothing is ever hidden, so a face can't get stuck invisible
 * the way a shared-layout crossfade can, and it runs the same in a
 * background tab.
 */
export function useFaceSlide(selectedId: number) {
  const root = useRef<HTMLDivElement>(null);
  const before = useRef<Map<number, DOMRect> | null>(null);

  /** Call right before changing the selection. */
  const snapshot = () => {
    const rects = new Map<number, DOMRect>();
    root.current?.querySelectorAll<HTMLElement>('[data-face]').forEach((el) => {
      rects.set(Number(el.dataset.face), el.getBoundingClientRect());
    });
    before.current = rects;
  };

  useLayoutEffect(() => {
    const from = before.current;
    before.current = null;
    if (!from || !root.current) return;
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    root.current.querySelectorAll<HTMLElement>('[data-face]').forEach((el) => {
      const was = from.get(Number(el.dataset.face));
      if (!was || typeof el.animate !== 'function') return;
      const now = el.getBoundingClientRect();
      if (now.width === 0 || was.width === 0) return;
      const dx = was.left - now.left;
      const dy = was.top - now.top;
      const scale = was.width / now.width;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(scale - 1) < 0.01) return;
      el.getAnimations().forEach((a) => a.cancel());
      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px) scale(${scale})` }, { transform: 'none' }],
        { duration: DURATION, easing: EASE }
      );
    });
  }, [selectedId]);

  return { root, snapshot };
}
